import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Offcanvas, Row, Spinner } from 'react-bootstrap';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import './AdminVendorList.css';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—');
const orDash = (v) => (v === 0 ? '0' : (v == null || String(v).trim() === '' ? '—' : String(v)));
const tel = (x) => (x ? <a href={`tel:${x}`}>{x}</a> : '—');
const mail = (x) => (x ? <a href={`mailto:${x}`}>{x}</a> : '—');
const toCSV = (rows) =>
  '\ufeff' + rows.map((r) => r.map((x) => `"${String(x ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
const getInitials = (name = '') =>
  String(name || '')
    .split(' ')
    .map((part) => part.trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'CM';
const getAvatarTone = (seed = '') => {
  const tones = ['blue', 'green', 'amber', 'violet', 'rose', 'sky'];
  const total = String(seed || '').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return tones[total % tones.length];
};
const toViewModel = (raw) => {
  const profile = raw?.profile || {};
  return {
    _id: String(raw?._id || ''),
    fullName: raw?.fullName || '',
    email: raw?.email || '',
    mobile: raw?.mobile || '',
    approved: !!raw?.approved,
    createdAt: raw?.createdAt || null,
    updatedAt: raw?.updatedAt || null,
    contractorName: profile.contractorName || raw?.fullName || '',
    millName: profile.millName || '',
    address: profile.address || '',
    contactPerson: profile.contactPerson || '',
    contactMobile: profile.contactMobile || raw?.mobile || '',
    status: profile.status === 'inactive' ? 'inactive' : 'active',
  };
};

const StatusPills = ({ approved, status }) => (
  <div className="vm-status-stack">
    <span className={`vm-status-pill ${approved ? 'approved' : 'pending'}`}>
      {approved ? 'Approved' : 'Pending'}
    </span>
    <span className={`vm-mini-pill ${status === 'inactive' ? 'waiting' : 'verified'}`}>
      Status: {status}
    </span>
  </div>
);

const AdminContractorList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [actionId, setActionId] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await API.get('/admin/contractors');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const vms = useMemo(() => rows.map(toViewModel).filter(Boolean), [rows]);
  const statusCounts = useMemo(
    () => ({
      all: vms.length,
      approved: vms.filter((row) => row.approved).length,
      pending: vms.filter((row) => !row.approved).length,
    }),
    [vms]
  );
  const filtered = useMemo(() => {
    const result = vms.filter((row) => {
      const matchStatus =
        status === 'all' ? true :
        status === 'approved' ? row.approved :
        status === 'pending' ? !row.approved : true;
      if (!matchStatus) return false;
      if (!debounced) return true;
      const blob = [
        row.contractorName,
        row.fullName,
        row.millName,
        row.mobile,
        row.email,
        row.contactPerson,
        row.contactMobile,
        row.address,
      ].filter(Boolean).join(' | ').toLowerCase();
      return blob.includes(debounced);
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    result.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (sortKey === 'createdAt' || sortKey === 'updatedAt') {
        return ((av ? new Date(av).getTime() : 0) - (bv ? new Date(bv).getTime() : 0)) * dir;
      }
      return String(av || '').localeCompare(String(bv || '')) * dir;
    });
    return result;
  }, [vms, debounced, status, sortKey, sortDir]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length, pageSize]);
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const runAction = async (id, action) => {
    try {
      setActionId(id);
      setError('');
      setMessage('');
      if (action === 'approve') await API.put(`/admin/contractors/${id}/approve`);
      if (action === 'reject') await API.put(`/admin/contractors/${id}/reject`);
      if (action === 'delete') {
        if (!window.confirm('Delete contractor?')) return;
        await API.delete(`/admin/contractors/${id}`);
      }
      setMessage(`Contractor ${action}d successfully`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || `Failed to ${action} contractor`);
    } finally {
      setActionId('');
    }
  };

  const handleSendWelcome = async (id) => {
    try {
      setActionId(id);
      setError('');
      setMessage('');
      const res = await API.post(`/admin/whatsapp/send-registration-welcome/${id}`);
      setMessage(res.data?.message || 'Welcome WhatsApp sent');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to send welcome WhatsApp');
    } finally {
      setActionId('');
    }
  };

  const exportCSV = () => {
    const csv = toCSV([
      ['Contractor Name', 'Full Name', 'Mill Name', 'Mobile', 'Email', 'Contact Person', 'Contact Mobile', 'Status', 'Approved', 'Address', 'Created At'],
      ...filtered.map((row) => ([
        row.contractorName,
        row.fullName,
        row.millName,
        row.mobile,
        row.email,
        row.contactPerson,
        row.contactMobile,
        row.status,
        row.approved ? 'Approved' : 'Pending',
        row.address,
        row.createdAt ? new Date(row.createdAt).toISOString() : '',
      ])),
    ]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contractors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearch('');
    setStatus('all');
  };

  return (
    <div className="vm-page">
      <div className="vm-container">
        <div className="vm-topbar">
          <div className="vm-title-wrap">
            <div className="vm-logo">CM</div>
            <div>
              <div className="vm-title">Contractor Management</div>
              <div className="vm-subtitle vm-subtitle-normal">Manage and monitor all contractors</div>
            </div>
          </div>
          <div className="vm-search vm-search-top d-none d-md-block">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Search contractors by name, mill, mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="vm-actions">
            <button className="vm-toolbar-btn vm-toolbar-btn-primary" onClick={exportCSV}>
              <span className="material-symbols-outlined">download</span>
              <span>Export CSV</span>
            </button>
            <button className="vm-toolbar-btn" onClick={load}>
              <span className="material-symbols-outlined">refresh</span>
              <span>Refresh</span>
            </button>
            <Button className="d-md-none vm-filters-btn" variant="secondary" size="sm" onClick={() => setShowFilters(true)}>
              Filters
            </Button>
            <button className="vm-toolbar-btn vm-toolbar-btn-primary d-none d-md-inline-flex" onClick={() => setShowFilters(true)}>
              <span className="material-symbols-outlined">filter_list</span>
              <span>Filters</span>
            </button>
          </div>
        </div>

        <div className="vm-toolbar">
          <div>
            <div className="vm-tabs">
              <button className={`vm-tab ${status === 'all' ? 'active' : ''}`} onClick={() => setStatus('all')}>
                <span>All Contractors</span>
                <span className="vm-tab-badge blue">{statusCounts.all}</span>
              </button>
              <button className={`vm-tab ${status === 'approved' ? 'active' : ''}`} onClick={() => setStatus('approved')}>
                <span>Approved</span>
                <span className="vm-tab-badge green">{statusCounts.approved}</span>
              </button>
              <button className={`vm-tab ${status === 'pending' ? 'active' : ''}`} onClick={() => setStatus('pending')}>
                <span>Pending</span>
                <span className="vm-tab-badge amber">{statusCounts.pending}</span>
              </button>
            </div>
          </div>

          <div className="vm-filter-row">
            <div className="vm-filter-stack">
              <div className="d-flex gap-2">
                <div className="vm-sort-label">Sort by</div>
                <Form.Select size="sm" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                  <option value="createdAt">Sort: Created</option>
                  <option value="contractorName">Sort: Name</option>
                  <option value="millName">Sort: Mill</option>
                  <option value="contactPerson">Sort: Contact</option>
                </Form.Select>
                <Form.Select size="sm" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </Form.Select>
              </div>
              <div className="vm-search d-md-none">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="text"
                  placeholder="Search contractors..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="bottom">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Filters</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form.Group className="mb-3">
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="name, mill, contact, mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sort By</Form.Label>
              <Form.Select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                <option value="createdAt">Created Date</option>
                <option value="contractorName">Contractor Name</option>
                <option value="millName">Mill Name</option>
                <option value="contactPerson">Contact Person</option>
              </Form.Select>
            </Form.Group>
            <div className="d-flex gap-2">
              <Button onClick={() => setShowFilters(false)}>Apply</Button>
              <Button variant="secondary" onClick={clearFilters}>Clear</Button>
            </div>
          </Offcanvas.Body>
        </Offcanvas>

        {message && <Alert variant="success" className="mt-3">{message}</Alert>}
        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

        <div className="mt-3">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : pageRows.length === 0 ? (
            <div className="text-center text-muted py-5 border rounded">No contractors found.</div>
          ) : (
            <Row xs={1} md={2} xl={3} className="g-3">
              {pageRows.map((r) => (
                <Col key={r._id}>
                  <div className="vm-entity-grid-card">
                    <div className="vm-entity-head">
                      <div className="vm-supplier-head">
                        <div className={`vm-avatar vm-avatar-${getAvatarTone(r._id || r.contractorName)}`}>
                          {getInitials(r.contractorName || r.fullName)}
                        </div>
                        <div className="vm-supplier-copy">
                          <div className="vm-person-name">{orDash(r.contractorName || r.fullName)}</div>
                          <div className="vm-company-name">{orDash(r.millName || r.fullName)}</div>
                          <div className="vm-code-chip">{orDash(r.contactPerson || 'Contractor')}</div>
                        </div>
                      </div>
                      <StatusPills approved={r.approved} status={r.status} />
                    </div>
                    <div className="vm-kv-grid">
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">smartphone</span>Mobile</span>
                        <span className="vm-v">{tel(r.mobile)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">mail</span>Email</span>
                        <span className="vm-v">{mail(r.email)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">person</span>Contact Person</span>
                        <span className="vm-v">{orDash(r.contactPerson)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">call</span>Contact Mobile</span>
                        <span className="vm-v">{tel(r.contactMobile)}</span>
                      </div>
                      <div className="vm-kv vm-kv-full">
                        <span className="vm-k"><span className="material-symbols-outlined">location_on</span>Address</span>
                        <span className="vm-v">{orDash(r.address)}</span>
                      </div>
                    </div>
                    <div className="vm-meta-row">
                      <div className="vm-meta-chip">
                        <span className="material-symbols-outlined">calendar_today</span>
                        <span>Created: {fmtDate(r.createdAt)}</span>
                      </div>
                      <div className="vm-meta-chip">
                        <span className="material-symbols-outlined">update</span>
                        <span>Updated: {fmtDate(r.updatedAt)}</span>
                      </div>
                    </div>
                    <div className="vm-po-grid">
                      <div className="vm-po-cell">
                        <span className="vm-k"><span className="material-symbols-outlined">domain</span>Mill Name</span>
                        <span className="vm-v">{orDash(r.millName)}</span>
                      </div>
                      <div className="vm-po-cell">
                        <span className="vm-k"><span className="material-symbols-outlined">verified_user</span>Approval</span>
                        <span className="vm-v">{r.approved ? 'Approved' : 'Pending approval'}</span>
                      </div>
                    </div>
                    <div className="vm-actions-wrap">
                      <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => navigate(`/admin/contractors/${r._id}/account`)}>
                        <span className="material-symbols-outlined">badge</span>
                        <span>View Account</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => navigate(`/admin/contractors/${r._id}/account#history`)}>
                        <span className="material-symbols-outlined">history</span>
                        <span>View History</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-green" disabled={actionId === r._id} onClick={() => handleSendWelcome(r._id)}>
                        <span className="material-symbols-outlined">chat</span>
                        <span>{actionId === r._id ? 'Sending...' : 'Send Welcome'}</span>
                      </Button>
                      {r.approved ? (
                        <Button size="sm" className="vm-action-btn vm-action-amber" disabled={actionId === r._id} onClick={() => runAction(r._id, 'reject')}>
                          <span className="material-symbols-outlined">close</span>
                          <span>{actionId === r._id ? 'Rejecting...' : 'Reject'}</span>
                        </Button>
                      ) : (
                        <Button size="sm" className="vm-action-btn vm-action-success" disabled={actionId === r._id} onClick={() => runAction(r._id, 'approve')}>
                          <span className="material-symbols-outlined">check</span>
                          <span>{actionId === r._id ? 'Approving...' : 'Approve'}</span>
                        </Button>
                      )}
                      <Button size="sm" className="vm-action-btn vm-action-danger-fill" disabled={actionId === r._id} onClick={() => runAction(r._id, 'delete')}>
                        <span className="material-symbols-outlined">delete</span>
                        <span>{actionId === r._id ? 'Deleting...' : 'Delete'}</span>
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {!loading && pageRows.length > 0 && (
            <div className="vm-pagination mt-3">
              <div className="small text-muted">
                Page {page} of {totalPages}
              </div>
              <div className="d-flex gap-2">
                <Form.Select size="sm" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ width: 110 }}>
                  {[10, 20, 50].map((s) => <option key={s} value={s}>{s}/page</option>)}
                </Form.Select>
                <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Prev
                </Button>
                <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContractorList;
